import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Typography,
  Chip,
  alpha,
  Fade,
  Zoom,
  LinearProgress,
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  PersonAdd, 
  Close,
  Email,
  Lock,
  Person,
  Shield,
  CheckCircle,
  Warning,
} from '@mui/icons-material';
import { createUser, hasAdminToken, fetchClients, fetchOrganizations } from '../api/admin';
import { Client, Organization } from '../types/admin';
import { useUser } from './UserContext';
import { formatApiError } from '../utils/helpers';

interface CreateUserDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const THEME = {
  // Backgrounds
  BG_PRIMARY: '#0a0a0f',
  BG_SECONDARY: '#1a1a1a',
  BG_TERTIARY: '#2d2d2d',
  SURFACE: '#121212',
  
  // Text
  TEXT_PRIMARY: '#ffffff',
  TEXT_SECONDARY: '#b0b0b0',
  TEXT_MUTED: '#707070',
  TEXT_LABEL: '#9e9e9e',
  
  // Accents - более мягкие оттенки
  ACCENT_PRIMARY: '#1976d2',
  ACCENT_SUCCESS: '#388e3c',
  ACCENT_WARNING: '#f57c00',
  ACCENT_ERROR: '#d32f2f',
  
  // Role Colors - менее яркие
  ROLE_SUPERADMIN: '#7b1fa2',
  ROLE_ADMIN: '#e65100',
  ROLE_EDITOR: '#1565c0',
  ROLE_USER: '#2e7d32',
  
  // Borders
  BORDER: '#333333',
  BORDER_LIGHT: '#404040',
};

// Общий стиль для TextField
const textFieldSx = {
  '& .MuiInputLabel-root': {
    color: THEME.TEXT_LABEL,
    fontSize: '0.95rem',
    fontWeight: 500,
    backgroundColor: THEME.BG_PRIMARY,
    px: 0.5,
    '&.Mui-focused': {
      color: THEME.ACCENT_PRIMARY,
      fontWeight: 600,
    },
    '&.Mui-error': {
      color: THEME.ACCENT_ERROR,
    }
  },
  '& .MuiOutlinedInput-root': {
    bgcolor: alpha(THEME.BG_SECONDARY, 0.6),
    color: THEME.TEXT_PRIMARY,
    '& input': {
      color: THEME.TEXT_PRIMARY,
    },
    '& fieldset': {
      borderColor: THEME.BORDER,
    },
    '&:hover': {
      bgcolor: alpha(THEME.BG_SECONDARY, 0.9),
      '& fieldset': {
        borderColor: THEME.BORDER_LIGHT,
      }
    },
    '&.Mui-focused': {
      bgcolor: THEME.BG_SECONDARY,
      '& fieldset': {
        borderColor: THEME.ACCENT_PRIMARY,
        borderWidth: '2px',
      }
    }
  },
  '& .MuiFormHelperText-root': {
    marginLeft: 0,
    fontSize: '0.75rem',
  }
};

export const CreateUserDialog: React.FC<CreateUserDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { user: currentUser } = useUser();
  const isSuperAdmin = currentUser?.role === 'super_admin' || currentUser?.role === 'superadmin';

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    role: 'user',
    client_id: 0,
    organization_id: 0,
    language: 'it', // Default language
  });

  const [clients, setClients] = useState<Client[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loadingData, setLoadingData] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Load clients and organizations when dialog opens
  React.useEffect(() => {
    if (open) {
      loadClientsAndOrganizations();
    }
  }, [open]);

  const loadClientsAndOrganizations = async () => {
    try {
      setLoadingData(true);
      const [clientsData, orgsData] = await Promise.all([
        fetchClients(),
        isSuperAdmin ? fetchOrganizations() : Promise.resolve([])
      ]);
      setClients(clientsData);
      setOrganizations(orgsData);
    } catch (err) {
      // Error loading data
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.username.trim()) {
      errors.username = 'Username è obbligatorio';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email è obbligatoria';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Email non valida';
    }

    if (!formData.password) {
      errors.password = 'Password è obbligatoria';
    } else if (formData.password.length < 6) {
      errors.password = 'Password deve essere almeno 6 caratteri';
    }

    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Le password non corrispondono';
    }

    if (formData.client_id === 0) {
      errors.client_id = 'Seleziona un cliente';
    }

    if (isSuperAdmin && formData.organization_id === 0) {
      errors.organization_id = 'Seleziona un\'organizzazione';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      const userData = {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
        role: formData.role,
        client_id: formData.client_id || undefined,
        organization_id: formData.organization_id || undefined,
        language: formData.language
      };

      await createUser(userData);

      // Reset form
      setFormData({
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        full_name: '',
        role: 'user',
        client_id: 0,
        organization_id: 0,
        language: 'it',
      });

      onSuccess();
      onClose();
    } catch (err: any) {
      let errorMessage = 'Errore durante la creazione dell\'utente';
      
      // Check if it's an admin token error
      if (err.message?.includes('Admin token not found')) {
        errorMessage = 'Sessione admin scaduta. Per favore rieffettua il login come amministratore.';
      } else if (err.response?.data?.detail) {
        errorMessage = formatApiError(err.response.data.detail);
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        full_name: '',
        role: 'user',
        client_id: 0,
        organization_id: 0,
        language: 'it',
      });
      setError(null);
      setFieldErrors({});
      onClose();
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'superadmin': return THEME.ROLE_SUPERADMIN;
      case 'admin': return THEME.ROLE_ADMIN;
      case 'editor': return THEME.ROLE_EDITOR;
      default: return THEME.ROLE_USER;
    }
  };

  const getPasswordStrength = () => {
    const password = formData.password;
    if (!password) return 0;
    let strength = 0;
    if (password.length >= 6) strength += 25;
    if (password.length >= 10) strength += 25;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 15;
    if (/[^a-zA-Z0-9]/.test(password)) strength += 10;
    return Math.min(strength, 100);
  };

  const passwordStrength = getPasswordStrength();
  const strengthColor = passwordStrength < 40 ? THEME.ACCENT_ERROR : passwordStrength < 70 ? THEME.ACCENT_WARNING : THEME.ACCENT_SUCCESS;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      TransitionComponent={Zoom}
      PaperProps={{
        sx: {
          bgcolor: THEME.BG_PRIMARY,
          backgroundImage: `
            radial-gradient(circle at 20% 10%, ${alpha(THEME.ACCENT_PRIMARY, 0.05)} 0%, transparent 50%),
            radial-gradient(circle at 80% 90%, ${alpha(THEME.ACCENT_SUCCESS, 0.04)} 0%, transparent 50%)
          `,
          border: `1px solid ${THEME.BORDER}`,
          borderRadius: 3,
          overflow: 'hidden',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: `linear-gradient(90deg, ${alpha(THEME.ACCENT_PRIMARY, 0.6)}, ${alpha(THEME.ACCENT_SUCCESS, 0.6)})`,
          }
        }
      }}
    >
      {loading && (
        <LinearProgress 
          sx={{ 
            position: 'absolute',
            top: 3,
            left: 0,
            right: 0,
            bgcolor: alpha(THEME.ACCENT_PRIMARY, 0.1),
            '& .MuiLinearProgress-bar': {
              bgcolor: THEME.ACCENT_PRIMARY
            }
          }} 
        />
      )}
      
      <DialogTitle 
        sx={{ 
          color: THEME.TEXT_PRIMARY,
          pb: 1,
          pt: 3,
          px: 3,
          background: `linear-gradient(180deg, ${THEME.BG_PRIMARY} 0%, transparent 100%)`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                background: `linear-gradient(135deg, ${alpha(THEME.ACCENT_PRIMARY, 0.9)}, ${alpha(THEME.ACCENT_SUCCESS, 0.9)})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 4px 16px ${alpha(THEME.ACCENT_PRIMARY, 0.2)}`,
              }}
            >
              <PersonAdd sx={{ color: '#fff', fontSize: 26 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                Crea Nuovo Utente
              </Typography>
              <Typography variant="caption" sx={{ color: THEME.TEXT_SECONDARY }}>
                Compila tutti i campi richiesti per creare un nuovo account
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            disabled={loading}
            sx={{
              color: THEME.TEXT_SECONDARY,
              bgcolor: THEME.BG_TERTIARY,
              width: 36,
              height: 36,
              '&:hover': {
                bgcolor: THEME.BORDER_LIGHT,
                transform: 'rotate(90deg)',
              },
              transition: 'all 0.3s',
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 2 }}>
        {!hasAdminToken() && (
          <Alert 
            severity="warning" 
            sx={{ 
              mb: 2,
              bgcolor: alpha(THEME.ACCENT_WARNING, 0.1),
              color: THEME.ACCENT_WARNING,
              border: `1px solid ${alpha(THEME.ACCENT_WARNING, 0.3)}`,
              '& .MuiAlert-icon': {
                color: THEME.ACCENT_WARNING
              }
            }}
            icon={<Warning />}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Sessione admin non trovata
            </Typography>
            <Typography variant="caption">
              Per creare nuovi utenti, è necessario rieffettuare il login come amministratore.
              Il token admin potrebbe essere scaduto.
            </Typography>
          </Alert>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 2 }}>
          {/* Username */}
          <Fade in timeout={300}>
            <TextField
              label="Username"
              value={formData.username}
              onChange={(e) => handleChange('username', e.target.value)}
              error={!!fieldErrors.username}
              helperText={fieldErrors.username}
              disabled={loading}
              required
              fullWidth
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person sx={{ color: THEME.TEXT_MUTED }} />
                  </InputAdornment>
                ),
              }}
              sx={textFieldSx}
            />
          </Fade>

          {/* Email */}
          <Fade in timeout={400}>
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              error={!!fieldErrors.email}
              helperText={fieldErrors.email}
              disabled={loading}
              required
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: THEME.TEXT_MUTED }} />
                  </InputAdornment>
                ),
              }}
              sx={textFieldSx}
            />
          </Fade>

          {/* Full Name */}
          <Fade in timeout={500}>
            <TextField
              label="Nome Completo"
              value={formData.full_name}
              onChange={(e) => handleChange('full_name', e.target.value)}
              disabled={loading}
              fullWidth
              placeholder="Opzionale"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person sx={{ color: THEME.TEXT_MUTED }} />
                  </InputAdornment>
                ),
              }}
              sx={textFieldSx}
            />
          </Fade>

          {/* Password with Strength Indicator */}
          <Fade in timeout={600}>
            <Box>
              <TextField
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                error={!!fieldErrors.password}
                helperText={fieldErrors.password}
                disabled={loading}
                required
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ color: THEME.TEXT_MUTED }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        disabled={loading}
                        sx={{ color: THEME.TEXT_MUTED }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={textFieldSx}
              />
              {formData.password && (
                <Box sx={{ mt: 1, px: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: THEME.TEXT_MUTED }}>
                      Forza password
                    </Typography>
                    <Typography variant="caption" sx={{ color: strengthColor, fontWeight: 600 }}>
                      {passwordStrength < 40 ? 'Debole' : passwordStrength < 70 ? 'Media' : 'Forte'}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={passwordStrength} 
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: alpha(strengthColor, 0.1),
                      '& .MuiLinearProgress-bar': {
                        bgcolor: strengthColor,
                        borderRadius: 3,
                        transition: 'all 0.3s',
                      }
                    }}
                  />
                </Box>
              )}
            </Box>
          </Fade>

          {/* Confirm Password */}
          <Fade in timeout={700}>
            <TextField
              label="Conferma Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={(e) => handleChange('confirmPassword', e.target.value)}
              error={!!fieldErrors.confirmPassword}
              helperText={fieldErrors.confirmPassword}
              disabled={loading}
              required
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: THEME.TEXT_MUTED }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {formData.confirmPassword && formData.password === formData.confirmPassword && (
                      <CheckCircle sx={{ color: THEME.ACCENT_SUCCESS, mr: 1 }} />
                    )}
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      disabled={loading}
                      sx={{ color: THEME.TEXT_MUTED }}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={textFieldSx}
            />
          </Fade>

          {/* Organization Selection (только для super_admin) */}
          {isSuperAdmin && (
            <Fade in timeout={700}>
              <FormControl 
                fullWidth 
                sx={textFieldSx}
                error={!!fieldErrors.organization_id}
              >
                <InputLabel>Organizzazione *</InputLabel>
                <Select
                  value={formData.organization_id}
                  onChange={(e) => handleChange('organization_id', Number(e.target.value))}
                  label="Organizzazione *"
                  disabled={loading || loadingData}
                >
                  <MenuItem value={0} disabled>Seleziona un'organizzazione</MenuItem>
                  {organizations.map((org) => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.name} {org.is_root && '(ROOT)'}
                    </MenuItem>
                  ))}
                </Select>
                {fieldErrors.organization_id && (
                  <Typography variant="caption" sx={{ color: THEME.ACCENT_ERROR, mt: 0.5 }}>
                    {fieldErrors.organization_id}
                  </Typography>
                )}
              </FormControl>
            </Fade>
          )}

          {/* Client Selection */}
          <Fade in timeout={750}>
            <FormControl 
              fullWidth 
              sx={textFieldSx}
              error={!!fieldErrors.client_id}
            >
              <InputLabel>Cliente *</InputLabel>
              <Select
                value={formData.client_id}
                onChange={(e) => handleChange('client_id', Number(e.target.value))}
                label="Cliente *"
                disabled={loading || loadingData}
              >
                <MenuItem value={0} disabled>Seleziona un cliente</MenuItem>
                {clients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.name}
                  </MenuItem>
                ))}
              </Select>
              {fieldErrors.client_id && (
                <Typography variant="caption" sx={{ color: THEME.ACCENT_ERROR, mt: 0.5 }}>
                  {fieldErrors.client_id}
                </Typography>
              )}
            </FormControl>
          </Fade>

          {/* Role Selection */}
          <Fade in timeout={800}>
            <Box>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: THEME.TEXT_SECONDARY, 
                  mb: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <Shield sx={{ fontSize: 18 }} />
                Seleziona Ruolo
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {['user', 'admin'].map((role) => (
                  <Chip
                    key={role}
                    label={role.charAt(0).toUpperCase() + role.slice(1)}
                    onClick={() => !loading && handleChange('role', role)}
                    disabled={loading}
                    sx={{
                      px: 2,
                      py: 2.5,
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      border: `2px solid ${formData.role === role ? getRoleColor(role) : THEME.BORDER}`,
                      bgcolor: formData.role === role ? alpha(getRoleColor(role), 0.15) : alpha(THEME.BG_SECONDARY, 0.5),
                      color: formData.role === role ? getRoleColor(role) : THEME.TEXT_SECONDARY,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: alpha(getRoleColor(role), 0.2),
                        borderColor: getRoleColor(role),
                        transform: 'translateY(-2px)',
                        boxShadow: `0 4px 12px ${alpha(getRoleColor(role), 0.3)}`,
                      },
                      '&.Mui-disabled': {
                        opacity: 0.5,
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Fade>

          {/* Language Selection */}
          <Fade in timeout={850}>
            <FormControl 
              fullWidth 
              sx={textFieldSx}
              error={!!fieldErrors.language}
            >
              <InputLabel>Lingua *</InputLabel>
              <Select
                value={formData.language}
                onChange={(e) => handleChange('language', e.target.value)}
                label="Lingua *"
                disabled={loading}
              >
                <MenuItem value="it">Italiano</MenuItem>
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="fr">Français</MenuItem>
                <MenuItem value="de">Deutsch</MenuItem>
                <MenuItem value="es">Español</MenuItem>
              </Select>
              {fieldErrors.language && (
                <Typography variant="caption" sx={{ color: THEME.ACCENT_ERROR, mt: 0.5 }}>
                  {fieldErrors.language}
                </Typography>
              )}
            </FormControl>
          </Fade>
        </Box>
      </DialogContent>
      
      <DialogActions 
        sx={{ 
          px: 3, 
          pb: 3,
          pt: 2,
          gap: 1.5,
          background: `linear-gradient(180deg, transparent 0%, ${alpha(THEME.BG_SECONDARY, 0.3)} 100%)`,
        }}
      >
        <Button 
          onClick={handleClose} 
          disabled={loading}
          sx={{ 
            color: THEME.TEXT_SECONDARY,
            px: 3,
            py: 1,
            borderRadius: 2,
            border: `1px solid ${THEME.BORDER}`,
            '&:hover': {
              bgcolor: alpha(THEME.BG_TERTIARY, 0.5),
              borderColor: THEME.BORDER_LIGHT,
            }
          }}
        >
          Annulla
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <PersonAdd />}
          sx={{
            px: 4,
            py: 1,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${alpha(THEME.ACCENT_PRIMARY, 0.85)}, ${alpha(THEME.ACCENT_SUCCESS, 0.85)})`,
            boxShadow: `0 4px 12px ${alpha(THEME.ACCENT_PRIMARY, 0.2)}`,
            fontWeight: 600,
            '&:hover': {
              background: `linear-gradient(135deg, ${THEME.ACCENT_SUCCESS}, ${THEME.ACCENT_PRIMARY})`,
              boxShadow: `0 6px 16px ${alpha(THEME.ACCENT_PRIMARY, 0.3)}`,
              transform: 'translateY(-2px)',
            },
            '&:active': {
              transform: 'translateY(0)',
            },
            '&.Mui-disabled': {
              background: alpha(THEME.TEXT_MUTED, 0.2),
              color: THEME.TEXT_MUTED,
            },
            transition: 'all 0.2s',
          }}
        >
          {loading ? 'Creazione in corso...' : 'Crea Utente'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
